/**
 * CounterpartyName (#3143) — names a linked `organization`, read-only.
 *
 * The READ-ONLY half of `CounterpartyPicker`'s `LinkedCounterparty`, extracted so
 * the ledger's Gegenpartei row (`LedgerFeed`) and the form's picker resolve an id
 * the same way rather than growing two answers to one question. The picker keeps
 * the Entfernen button; this has no mutation surface at all.
 *
 * **Mount it conditionally.** This component IS a read — `useEntitiesByIds` fires
 * on mount. `EntityTile` renders its children eagerly, so a Gegenpartei row mounted
 * ungated would be an N+1 across the ledger page AND would break every existing
 * `LedgerFeed` test, which render with no `QueryClientProvider`. `LedgerFeed` gates
 * it on `isExpanded`, exactly like `TransactionTwinLinks`. That is the same
 * conditionally-mounted-child discipline `CounterpartyPicker`'s own header states.
 *
 * **Three states, not two.** The raw-id fallback exists for a link whose org the
 * read could not name (deleted, or outside the page) and must stay VISIBLE — the
 * link is real. But letting it double as the LOADING state flashes a bare UUID at
 * the farmer for one round trip before snapping to the name, which reads as
 * corruption.
 */
import { useEntitiesByIds } from '../../../hooks/useTwin';
import { organizationOptions } from './counterparty';

const ENTITY_TYPE = 'organization';

export interface CounterpartyNameProps {
  /** The linked `organization` entity id. */
  id: string;
  /**
   * Provenance of the link, when known — the seam #3142 (Phase 2) wires its
   * „automatisch zugeordnet" marker into. Since #3160 the ledger passes the real
   * value: a bank `payed` record's `counterparty_id_source` (closed vocabulary
   * declared by #3142 in `config/ontology/cash_flow.yaml` — `invoice_reference` |
   * `curated_map`, opaque pass-through here) travels `toBank → bankToRecord →
   * LedgerFeed.counterpartyOf`, read off the SAME record whose id won the
   * precedence walk. Rendering stays a `data-counterparty-source` attribute only;
   * the visible marker remains Phase 2's. Still deliberately NOT lifted through
   * `cashFlowToRecord` — its Python byte-mirror (`liquiditaet_projection.py`)
   * gained the lift in #3142, so closing that second drift is an explicit
   * mirror-catch-up decision (#3160's decide-loudly note), not this component's.
   */
  source?: string;
  /** Class for the name span. Defaults to the ledger's own. */
  className?: string;
  /**
   * When set, the organization's `org_type` is rendered as a sibling span in this
   * class — the picker's "selected" chrome. Omitted by the ledger, whose Gegenpartei
   * row wants the name alone. Two spans as a FRAGMENT, deliberately: it keeps
   * `CounterpartyPicker`'s DOM byte-identical across this extraction, which its
   * parity snapshot depends on.
   */
  sublabelClassName?: string;
}

export default function CounterpartyName({
  id,
  source,
  className = 'liquiditaetsplanung-ledger__counterparty',
  sublabelClassName,
}: CounterpartyNameProps) {
  const { data, isLoading } = useEntitiesByIds(ENTITY_TYPE, [id]);
  const resolved = organizationOptions(data?.entities, data?.primary_key).find((o) => o.id === id);
  const label = resolved?.label ?? (isLoading ? 'Wird geladen …' : id);
  return (
    <>
      <span className={className} {...(source ? { 'data-counterparty-source': source } : {})}>
        {label}
      </span>
      {sublabelClassName && resolved?.sublabel && (
        <span className={sublabelClassName}>{resolved.sublabel}</span>
      )}
    </>
  );
}
